import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getNews, toggleLike, newsImageUrl, timeAgo, CATEGORIES, deleteNews } from "../api/news";
import NewsContent from "../components/news/NewsContent";
import NewsComments from "../components/news/NewsComments";
import { REACTION_ICONS, REACTION_LIST } from "../components/news/ReactionIcons";
import { EyeIcon, CatIcon } from "../components/news/StatIcons";
import { Button, EmptyState, Skeleton, fmtNum, plural } from "../components/ui";
import usePageTitle from "../hooks/usePageTitle";
import "../styles/news.css";

const MIN_USER_ID = 1;

export default function NewsDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const isMin = user?.userId === MIN_USER_ID;

  const [news, setNews] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [tick, setTick] = useState(0);   // триггер «Повторить»

  usePageTitle(news ? `${news.title} — Блог МИНа` : "Блог МИНа");

  useEffect(() => {
    setLoading(true);
    getNews(id)
      .then(setNews)
      .catch((e) => setErr(e.message))
      .finally(() => setLoading(false));
  }, [id, tick]);

  const [actionError, setActionError] = useState("");
  const [deleting, setDeleting] = useState(false);

  async function react(reaction) {
    if (!isAuthenticated) { navigate("/login"); return; }
    setActionError("");
    try {
      const r = await toggleLike(id, reaction);
      setNews((n) => ({ ...n, likes_count: r.likes_count, is_liked: r.liked, my_reaction: r.reaction }));
    } catch {
      // Был пустой catch: клик по реакции при обрыве сети не давал ничего,
      // и человек жал снова, думая, что кнопка сломана.
      setActionError("Не получилось поставить реакцию. Попробуй ещё раз.");
    }
  }

  /* Удаление. Прежний `catch {}` был худшим из всех немых: при отказе
     (403, обрыв) не происходило ВООБЩЕ ничего — окно подтверждения уже
     закрыто, статья на месте, сообщения нет. Человек уходил в уверенности,
     что новость удалена. */
  async function onDelete() {
    if (deleting) return;
    if (!window.confirm("Удалить новость? Это необратимо.")) return;
    setDeleting(true);
    setActionError("");
    try {
      await deleteNews(id);
      navigate("/app/news");
    } catch (e) {
      setActionError(e?.message || "Не удалось удалить новость.");
      setDeleting(false);
    }
  }

  if (loading)
    return (
      <div className="nws-page nws-page--article">
        <div className="nws-container">
          <Skeleton h={34} w="70%" />
          <Skeleton h={16} w="40%" style={{ marginTop: 14 }} />
          <Skeleton h={280} style={{ marginTop: 22, borderRadius: "var(--r-lg)" }} />
        </div>
      </div>
    );

  /* 404 и сетевую ошибку раньше склеивали в одно «Новость не найдена»:
     при обрыве связи человеку сообщали, что статьи не существует. */
  if (err || !news) {
    const notFound = /404|не найден/i.test(String(err || ""));
    return (
      <div className="nws-page nws-page--article">
        <div className="nws-container">
          <EmptyState
            title={notFound ? "Новость не найдена" : "Не получилось загрузить"}
            desc={
              notFound
                ? "Возможно, её удалили или ссылка устарела."
                : "Проверь соединение и попробуй ещё раз."
            }
            action={
              notFound ? (
                <Button variant="primary" onClick={() => navigate("/app/news")}>
                  Ко всем новостям
                </Button>
              ) : (
                <Button variant="primary" onClick={() => setTick((t) => t + 1)}>
                  Повторить
                </Button>
              )
            }
          />
        </div>
      </div>
    );
  }

  const cat = CATEGORIES.find((c) => c.id === news.category);
  const cover = newsImageUrl(news.cover_image);

  return (
    <div className="nws-page nws-page--article">
      <header className="nws-topbar">
        <div className="nws-topbar-inner">
          <Link to="/app/news" className="nws-back">← Все новости</Link>
          {isMin && (
            <span className="nws-article-actions">
              <Button variant="ghost" size="sm" onClick={() => navigate(`/app/news/edit/${id}`)}>
                Редактировать
              </Button>
              <Button variant="danger" size="sm" onClick={onDelete} loading={deleting}>
                Удалить
              </Button>
            </span>
          )}
        </div>
      </header>

      <div className="nws-container">
        {cover && <img className="nws-article-cover" src={cover} alt="" />}
        <h1 className="nws-article-title">{news.title}</h1>
        <div className="nws-article-meta">
          {news.author && (
            <span className="nws-author">
              {news.author.avatar
                ? <img src={`/api-user/assets/profile/${news.author.avatar}`} alt="" onError={(e)=>e.target.style.display="none"} />
                : <span className="nws-author-noimg">{(news.author.name||"М")[0]}</span>}
              {news.author.name}
            </span>
          )}
          {cat && <span className="nws-badge"><CatIcon id={cat.id} /> {cat.label}</span>}
          <span>{timeAgo(news.published_at)}</span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><EyeIcon /> <span className="ui-num">{fmtNum(news.views_count)}</span></span>
        </div>

        <NewsContent blocks={news.content_blocks} />

        <div className="nws-reactions">
          {REACTION_LIST.map((r) => {
            const Icon = REACTION_ICONS[r.id];
            const active = news.my_reaction === r.id;
            return (
              <button key={r.id} title={r.label}
                      className={`nws-react-btn ${active ? "is-active" : ""}`}
                      onClick={() => react(r.id)}>
                <Icon active={active} />
              </button>
            );
          })}
          <span className="nws-likes-count ui-num">{fmtNum(news.likes_count)} {plural(news.likes_count, "реакция", "реакции", "реакций")}</span>
        </div>

        {/* Ошибка действия — рядом с самим действием, а не поверх страницы:
            статья на месте, сообщение относится только к реакции/удалению. */}
        {actionError && <div className="nws-action-error">{actionError}</div>}

        <NewsComments newsId={news.id} onCountChange={(d) => setNews((n) => ({ ...n, comments_count: (n.comments_count||0)+d }))} />
      </div>
    </div>
  );
}
